import { useQuery } from '@tanstack/react-query'
import type { OnChangeFn, PaginationState, SortingState } from '@tanstack/react-table'
import { Wallet } from 'lucide-react'

import { tellerSessionListQueryOptions } from '@/api/teller-api'
import type { TellerSession, TellerSessionStatus } from '@/api/teller-types'
import { DataTable, dataTableColumns, type DataTableColumn } from '@/components/data-table'
import { MoneyDisplay } from '@/components/money-display'
import { PageHeader } from '@/components/page-header'
import { StatusBadge } from '@/components/status-badge'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { TELLER_SESSION_STATUSES, sessionStatusLabel } from '@/features/teller/constants'
import { TELLER_PATHS, useTellerNavigate, useTellerRawSearch } from '@/features/teller/navigation'
import { TELLER_PERMISSIONS, useTellerPermissions } from '@/features/teller/permissions'
import { PermissionNotice } from '@/features/teller/teller-panels'
import { TellerLink } from '@/features/teller/teller-link'
import { usePostingDay } from '@/features/teller/use-posting-day'
import { formatBusinessDate, formatDateTime } from '@/lib/datetime'

import { DEFAULT_PAGE_SIZE, tellerSessionListSearchSchema } from './search'

/**
 * The day's shifts — the checklist the branch day close waits on.
 *
 * The date filter starts empty rather than at today. The ledger's date is the branch's, it arrives
 * from `/ledger/business-day/current`, and it is null whenever no day is open; a filter primed from
 * the browser's clock would show the wrong day's shifts at any branch still working in yesterday,
 * and would show a confident date at a branch that has none. What the screen does instead is offer
 * the ledger's day as a button, labelled with the date it actually is.
 */

const UNSORTED: SortingState = []
const ignoreSortingChange: OnChangeFn<SortingState> = () => undefined

const helper = dataTableColumns<TellerSession>()

const columns: DataTableColumn<TellerSession>[] = helper.columns([
  helper.accessor('tillNumber', {
    header: 'Till',
    enableSorting: false,
    cell: (context) => <span className="font-mono text-xs">{context.row.original.tillNumber}</span>,
    meta: { noWrap: true, widthClass: 'w-24', card: 'lead' },
  }),
  helper.accessor('tellerUsername', {
    header: 'Teller',
    enableSorting: false,
    cell: (context) => (
      <span className="font-medium text-heading">{context.row.original.tellerUsername}</span>
    ),
    meta: { card: 'title' },
  }),
  helper.accessor('businessDate', {
    header: 'Business date',
    enableSorting: false,
    cell: (context) => (
      <span className="text-xs">{formatBusinessDate(context.row.original.businessDate)}</span>
    ),
    meta: { noWrap: true, widthClass: 'w-36', card: 'lead' },
  }),
  helper.accessor('expectedClosingCashMinor', {
    header: 'Expected',
    enableSorting: false,
    cell: (context) => (
      <MoneyDisplay
        value={context.row.original.expectedClosingCashMinor}
        currency={context.row.original.currencyCode}
      />
    ),
    meta: { align: 'right', widthClass: 'w-36' },
  }),
  helper.accessor('declaredClosingCashMinor', {
    header: 'Declared',
    enableSorting: false,
    // Null until the teller declares, which is most of the working day. A dash, never a zero:
    // "nobody has counted" and "counted nothing" are different states of the same drawer.
    cell: (context) => (
      <MoneyDisplay
        value={context.row.original.declaredClosingCashMinor}
        currency={context.row.original.currencyCode}
      />
    ),
    meta: { align: 'right', widthClass: 'w-36' },
  }),
  helper.accessor('overShortMinor', {
    header: 'Over / short',
    enableSorting: false,
    cell: (context) => (
      <MoneyDisplay
        value={context.row.original.overShortMinor}
        currency={context.row.original.currencyCode}
        signed
        colourNegative
        emphasis="strong"
      />
    ),
    meta: { align: 'right', widthClass: 'w-36', card: 'lead' },
  }),
  helper.accessor('status', {
    header: 'Status',
    enableSorting: false,
    cell: (context) => <StatusBadge status={context.row.original.status} />,
    meta: { widthClass: 'w-36' },
  }),
  helper.accessor('closedAtUtc', {
    header: 'Closed',
    enableSorting: false,
    cell: (context) =>
      context.row.original.closedAtUtc === null ? (
        <span className="text-xs text-muted">Still open</span>
      ) : (
        <span className="text-xs">{formatDateTime(context.row.original.closedAtUtc)}</span>
      ),
    meta: { noWrap: true, widthClass: 'w-44' },
  }),
])

export function TellerSessionListPage() {
  const navigate = useTellerNavigate()
  const rawSearch = useTellerRawSearch()
  const permissions = useTellerPermissions()
  const day = usePostingDay()

  const search = tellerSessionListSearchSchema.parse(rawSearch)
  const page = search.page ?? 1
  const pageSize = search.pageSize ?? DEFAULT_PAGE_SIZE

  const query = useQuery({
    ...tellerSessionListQueryOptions({
      tillId: search.tillId,
      businessDate: search.businessDate,
      status: search.status,
      page,
      pageSize,
    }),
    enabled: permissions.canViewTills,
  })

  const updateSearch = (patch: Partial<typeof search>) => {
    navigate(TELLER_PATHS.sessions, {
      search: {
        tillId: search.tillId,
        businessDate: search.businessDate,
        status: search.status,
        page: search.page,
        pageSize: search.pageSize,
        ...patch,
      },
    })
  }

  const onPaginationChange: OnChangeFn<PaginationState> = (updater) => {
    const current: PaginationState = { pageIndex: page - 1, pageSize }
    const next = typeof updater === 'function' ? updater(current) : updater
    updateSearch({ page: next.pageIndex + 1, pageSize: next.pageSize })
  }

  if (!permissions.canViewTills) {
    return (
      <>
        <PageHeader title="Shifts" description="Teller shifts, for the day-close checklist." />
        <PermissionNotice permission={TELLER_PERMISSIONS.tillView} what="Teller shifts" />
      </>
    )
  }

  const unbalanced = (query.data?.items ?? []).filter(
    (session) => session.overShortMinor !== null && session.overShortMinor !== 0,
  ).length

  return (
    <>
      <PageHeader
        title="Shifts"
        description="Every drawer worked today, what it should hold, and what it was declared at. The branch day close waits on this."
        actions={
          <Button asChild variant="secondary">
            <TellerLink to={TELLER_PATHS.counter}>
              <Wallet />
              Go to the counter
            </TellerLink>
          </Button>
        }
      />

      {unbalanced > 0 ? (
        <Alert tone="warning">
          <div className="space-y-1">
            <AlertTitle>
              {unbalanced} shift{unbalanced === 1 ? '' : 's'} on this page did not balance
            </AlertTitle>
            <AlertDescription>
              An over or short posts to the ledger when the close is countersigned. Open the shift to
              read the teller&apos;s note and the count sheet behind the figure.
            </AlertDescription>
          </div>
        </Alert>
      ) : null}

      <div className="flex flex-wrap items-end gap-3">
        <div className="w-56 space-y-1.5">
          <Label htmlFor="session-date">Business date</Label>
          <Input
            id="session-date"
            type="date"
            value={search.businessDate ?? ''}
            onChange={(event) =>
              updateSearch({
                businessDate: event.target.value === '' ? undefined : event.target.value,
                page: undefined,
              })
            }
            aria-describedby="session-date-help"
          />
          <p id="session-date-help" className="text-xs text-muted">
            {day.businessDate
              ? 'Empty shows every date the server returns.'
              : 'This branch has no day open, so there is no ledger date to default to.'}
          </p>
        </div>

        {day.businessDate && search.businessDate !== day.businessDate ? (
          <Button
            variant="secondary"
            size="sm"
            onClick={() => updateSearch({ businessDate: day.businessDate ?? undefined, page: undefined })}
          >
            Today&apos;s ledger day · {formatBusinessDate(day.businessDate)}
          </Button>
        ) : null}

        <div className="w-48 space-y-1.5">
          <Label htmlFor="session-status">Status</Label>
          <Select
            value={search.status ?? 'all'}
            onValueChange={(value) =>
              updateSearch({
                status: value === 'all' ? undefined : (value as TellerSessionStatus),
                page: undefined,
              })
            }
          >
            <SelectTrigger id="session-status">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Any status</SelectItem>
              {TELLER_SESSION_STATUSES.map((value) => (
                <SelectItem key={value} value={value}>
                  {sessionStatusLabel(value)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {search.tillId ? (
          <Button variant="ghost" size="sm" onClick={() => updateSearch({ tillId: undefined, page: undefined })}>
            Clear the till filter
          </Button>
        ) : null}
      </div>

      <DataTable
        columns={columns}
        data={query.data?.items}
        rowCount={query.data?.totalCount}
        pagination={{ pageIndex: page - 1, pageSize }}
        onPaginationChange={onPaginationChange}
        sorting={UNSORTED}
        onSortingChange={ignoreSortingChange}
        isLoading={query.isPending}
        isFetching={query.isFetching}
        error={query.isError ? query.error : null}
        onRetry={() => void query.refetch()}
        getRowId={(row) => row.id}
        onRowActivate={(session) => navigate(TELLER_PATHS.session(session.id))}
        pageSizeOptions={[DEFAULT_PAGE_SIZE, 50, 100]}
        emptyTitle="No shifts"
        emptyDescription="No drawer has been worked under these filters. A field till never appears here — it is assigned for a day rather than opened as a shift."
      />
    </>
  )
}
